import { Component } from '@verndale/core';
import { breakpoints } from '../../helpers/variables';
import HeaderSearch from './HeaderSearch';
import LanguageSelector from './LanguageSelector';
import { EventBusSingleton } from '../../animation/eventBus';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { ElementOrSelector, stagger, timeline } from 'motion';

class UtilityNavigation extends Component {
  MOTION_ZERO_DURATION = 0.000001;
  breakpoint = window.matchMedia(
    `(min-width: ${breakpoints.tabletLandscape}px)`
  );
  breakpointAnimation = window.matchMedia(
    `(min-width: ${breakpoints.tablet}px)`
  );
  headerSearch: HeaderSearch | null = null;
  languageSelector: LanguageSelector | null = null;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  initialDelay = 0.75;
  viewportWidth = window.innerWidth;

  constructor(el: HTMLElement) {
    super(el);
    if (this.dom.headerSearch)
      this.headerSearch = new HeaderSearch(
        this.dom.headerSearch as HTMLElement
      );
    if (this.dom.languageSelector)
      this.languageSelector = new LanguageSelector(
        this.dom.languageSelector as HTMLElement
      );
    const hasMastheadPlayed = localStorage.getItem(
      'manhattan_masthead-animation'
    );
    if (!hasMastheadPlayed && this.breakpointAnimation.matches) {
      if (!this.reducedMotion.matches && this.breakpoint.matches) {
        if (!document.querySelector('.intro-animation-overlay')) {
          this.initialDelay = 0;
          this.animateIn();
        } else {
          EventBusSingleton.subscribe(
            'introAnimationOverlay:hide',
            this.animateIn.bind(this)
          );
        }
      }
    }
    EventBusSingleton.subscribe(
      'utilityNavigation:closingHeaderSearch',
      this.handleCloseSearch.bind(this)
    );
    EventBusSingleton.subscribe(
      'utilityNavigation:closingLanguageSelector',
      () => {
        (this.dom.languageButton as HTMLElement).classList.remove(
          'language-selector--open'
        );
      }
    );
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      searchButton: this.el.querySelector<HTMLElement>(
        '.utility-navigation__search-button'
      ),
      loginButton: this.el.querySelector<HTMLElement>(
        '.utility-navigation__login-button'
      ),
      languageButton: this.el.querySelector<HTMLElement>(
        '.utility-navigation__language-and-region-button'
      ),
      headerSearch: this.el.querySelector<HTMLElement>('.header-search'),
      languageSelector:
        this.el.querySelector<HTMLElement>('.language-selector'),
      background: this.el.querySelector<HTMLElement>(
        '.utility-navigation__background'
      )
    };
  }

  addListeners() {
    (this.dom.searchButton as HTMLElement)?.addEventListener(
      'click',
      this.handleSearchClick.bind(this)
    );
    (this.dom.languageButton as HTMLElement)?.addEventListener(
      'click',
      this.handleLanguageSelectorClick.bind(this)
    );
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  animateIn() {
    const staggerItems = (this.dom.el as HTMLElement).querySelectorAll(
      '.utility-navigation__section > *:not(.header-search):not(.language-selector)'
    ) as NodeListOf<HTMLElement>;

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push({ name: 'stagger-items', at: 0.75 });
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        transform: ['scale(0, 1)', 'scale(1, 1)']
      },
      {
        duration: 1,
        at: 'start'
      }
    ]);
    sequence.push([
      staggerItems,
      {
        opacity: [0, 1],
        transform: ['translate(0, -100%)', 'translate(0, 0)']
      },
      {
        duration: 0.25,
        at: 'stagger-items',
        delay: stagger(0.1)
      }
    ]);
    timeline(sequence, { delay: this.initialDelay });
  }

  handleResize() {
    if (window.innerWidth !== this.viewportWidth) {
      this.headerSearch?.closeSearch();
      this.languageSelector?.closeMenu();
      (this.dom.el as HTMLElement).classList.remove('language-selector--open');
      this.viewportWidth = window.innerWidth;
      this.breakpoint = window.matchMedia(
        `(min-width: ${breakpoints.tabletLandscape}px)`
      );
      this.breakpointAnimation = window.matchMedia(
        `(min-width: ${breakpoints.tablet}px)`
      );
    }
  }

  handleCloseSearch() {
    (this.dom.searchButton as HTMLElement).classList.remove(
      'header-search--open'
    );
  }

  async handleSearchClick() {
    const isOpen = (this.dom.headerSearch as HTMLElement).classList.contains(
      'header-search--open'
    );
    if (!isOpen) {
      EventBusSingleton.publish('utilityNavigation:openingHeaderSearch');
      await this.headerSearch?.openSearch();
      (this.dom.searchButton as HTMLElement).classList.add(
        'header-search--open'
      );
      (this.dom.languageButton as HTMLElement).classList.remove(
        'language-selector--open'
      );
    } else {
      EventBusSingleton.publish('utilityNavigation:closingHeaderSearch');
      this.headerSearch?.closeSearch();
      this.languageSelector?.closeMenu();
      (this.dom.searchButton as HTMLElement).classList.remove(
        'header-search--open'
      );
    }
  }

  async handleLanguageSelectorClick() {
    const isOpen = (
      this.dom.languageSelector as HTMLElement
    ).classList.contains('language-selector--open');
    if (!isOpen) {
      EventBusSingleton.publish('utilityNavigation:openingLanguageSelector');
      this.languageSelector?.openMenu();
      (this.dom.languageButton as HTMLElement).classList.add(
        'language-selector--open'
      );
      (this.dom.searchButton as HTMLElement).classList.remove(
        'header-search--open'
      );
      (this.dom.el as HTMLElement).classList.add('language-selector--open');
    } else {
      EventBusSingleton.publish('utilityNavigation:closingLanguageSelector');
      (this.dom.languageButton as HTMLElement).classList.remove(
        'language-selector--open'
      );
      (this.dom.el as HTMLElement).classList.remove('language-selector--open');
      this.languageSelector?.closeMenu();
    }
  }
}

export default UtilityNavigation;
